import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { PlayArrow as RunIcon } from '@mui/icons-material';
import type { Experiment } from '../../types/experiment';

interface ExperimentRunConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  experiment: Experiment | null;
  isLoading?: boolean;
  validationErrors?: string[];
}

const ExperimentRunConfirmationDialog: React.FC<ExperimentRunConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  experiment,
  isLoading = false,
  validationErrors = []
}) => {
  if (!experiment) return null;

  const enabledCameras = experiment.cameras_enabled?.filter(c => c.enabled) || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Confirm Experiment Run
      </DialogTitle>
      <DialogContent>
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Cannot start experiment:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Typography>
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Please review the experiment configuration before starting:
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Experiment Configuration</Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Test ID:</strong> {experiment.test_id || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Profile ID:</strong> {experiment.profile_id || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Number of Profiles:</strong> {experiment.num_profiles}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>Chamber Enabled:</strong> {experiment.chamber_enabled ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2"><strong>Cameras Enabled:</strong> {enabledCameras.length}</Typography>
              <Typography variant="body2"><strong>Experiment Status:</strong> {experiment.exp_status}</Typography>
            </Grid>
          </Grid>
          
          {experiment.raspi_id && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                RASPI Configuration:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  • RASPI ID: {experiment.raspi_id}
                </Typography>
              </Box>
            </Box>
          )}
          
          {experiment.chamber_enabled && experiment.chamber_id && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Chamber Configuration:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  • Chamber ID: {experiment.chamber_id}
                </Typography>
              </Box>
            </Box>
          )}
          
          {enabledCameras.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Enabled Cameras:
              </Typography>
              <Box sx={{ pl: 2 }}>
                {enabledCameras.map((camera, index) => (
                  <Typography key={index} variant="body2" color="text.secondary">
                    • {camera.camera_id}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="primary"
          disabled={isLoading || validationErrors.length > 0}
          startIcon={isLoading ? <CircularProgress size={16} /> : <RunIcon />}
        >
          {isLoading ? 'Starting...' : 'RUN'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExperimentRunConfirmationDialog; 